'use strict';

import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import moment from "moment";

import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {isEmpty, isFalse, isTrue} from "@uw-it-sis/lib-react/lib/Utils";
import {getGeneralPlaceholderName} from "@uw-it-sis/lib-react-myplan/lib/GeneralPlaceHolderConstants";
import GenEdUtils from "@uw-it-sis/lib-react-myplan/lib/GenEdUtils";

import PlanItemActions from "./PlanItemActions";
import CreditBadge from "./CreditBadge";
import planItemModalStore from "../registration/PlanItemModalStore";
import {DateFormatString, PlanItemType, PlanViewType} from "../lib/Constants";

/**
 * Component for displaying a plan item placeholder
 */
const PlanItemPlaceholder = ({item = {}, type, viewType, openForPlanning}) => {
    if ( isEmpty(item) ) {
        // This shouldn't happen, but just in case
        return null;
    }

    let planItem = item;

    let placeHolderName = getGeneralPlaceholderName(planItem.placeHolderCode, planItem.placeHolderValue);

    let placeHolderPrefix;
    if (planItem.planItemDataObject.refObjType === "uw.course.genedrequirement") {
        placeHolderPrefix = "Gen Ed Req: ";
    }

    const toggleNote = (e) => {
        e.target.classList.toggle("text-truncate");
    };

    let noteBody;

    if ( type !== PlanItemType.recommended && planItem.note !== null && planItem.note !== "" ) {
        noteBody =
            <Card.Body className="pt-0 px-3 pb-2">
                <p tabIndex={0}
                   className="PlanItemNote fst-italic mb-0 text-truncate"
                   style={{cursor: "pointer"}}
                   onFocus={(e) => toggleNote(e)}
                   onBlur={(e) => toggleNote(e)}
                   title={planItem.note}>
                    {planItem.note}
                </p>
            </Card.Body>
    }

    let planItemActions =
        <PlanItemActions title={`${placeHolderPrefix ?? ""}${placeHolderName}`}
                         data={planItem}
                         type={type}
                         placeholder={true}
                         openForPlanning={openForPlanning}
                         sectionsAvailable={false} />;

    //Open the Placeholder summary modal
    const showModal =() => {
        planItemModalStore.openModal('acceptPlaceholder', planItem, {context: "PlanView-modal"});
    };

    // Display a message when it is of type recommended and the view is a plan view
    let recommendedBody = null;
    if ( viewType === PlanViewType.plan && type === PlanItemType.recommended) {
        // If the plan item is a recommendation, and the recommendation isn't already on the plan, and the user is not an adviser
        // show the accept recommendation link
        let recommendedLink = null;
        if ( ! appStore.isAdviser() ) {
            if ( planItem.hasOwnProperty("recommendationOnPlan") && isFalse(planItem.recommendationOnPlan) && openForPlanning ) {
                recommendedLink =
                    <Button variant="inline-link"
                            onClick={showModal}
                            aria-haspopup="true"
                            className="text-decoration-underline mt-3">
                        Add the recommendation to plan
                    </Button>;
            } else {
                // The only action item that would be in the PlanItemActions for a recommended item ( for now )
                // is to accept the recommendation
                // If the link is not going to be displayed to accept the recommendation, do not provide the actions,
                // since the only action to do is the one you are trying to hide
                planItemActions = null;
            }
        }

        let recommendNote = null;
        if ( planItem.note && planItem.note !== "" ) {
            // Single quotes around adviser note
            recommendNote =
                <p tabIndex={0}
                   className="PlanItemNote fst-italic mt-3 mb-0 text-truncate"
                   style={{cursor: "pointer"}}
                   onFocus={(e) => toggleNote(e)}
                   onBlur={(e) => toggleNote(e)}
                   title={planItem.note}>
                    '{planItem.note}'
                </p>;
        }

        recommendedBody =
            <Card.Body className="pt-0 px-3 pb-2">
                <p className="mb-0 lh-sm">
                    <small>{item.adviserName} recommended this on {moment(item.planItemDataObject.dateAdded).format(DateFormatString)}</small>
                </p>
                {recommendNote}
                {recommendedLink}
            </Card.Body>
    }

    return (
        <Card border="light-gray">
            <Card.Header className="d-flex justify-content-between align-items-center bg-transparent py-2 ps-3 pe-1">
                <div className="text-truncate">
                    <span className="align-middle lead fw-bold">
                        {placeHolderPrefix ? <abbr title={`General Education Requirement: ${GenEdUtils.getTitleFromCode(placeHolderName)}`}>{placeHolderPrefix}{placeHolderName}</abbr> : placeHolderName}
                    </span>
                    <CreditBadge credit={planItem.placeHolderCredit} className="ms-3" />
                </div>
                {planItemActions}
            </Card.Header>
            {recommendedBody}
            {noteBody}
        </Card>
    );
}

PlanItemPlaceholder.propTypes = {
    item: PropTypes.object.isRequired, // the plan item object (not to be confused with planItemDataObject which the item contains)
    viewType: PropTypes.oneOf(Object.keys(PlanViewType)), // which view is displaying the component, regView or planView
    type: PropTypes.oneOf(Object.keys(PlanItemType)).isRequired // the type of plan item (planned, backup, registered, recommended)
};

export default PlanItemPlaceholder;